import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AssessmentIntegrityEvent, AssessmentIntegrityEventFilter, AssessmentIntegrityEventType } from '../../models/assessment-integrity-event';
import { assessmentIntegrityService } from '../../services/assessmentIntegrityService';
import { formatDateToLocal } from '../../utils/dateUtils';
import styles from './AssessmentIntegrity.module.css';

const PAGE_SIZE = 25;

const eventTypes: AssessmentIntegrityEventType[] = [
  'AssessmentOpened',
  'AssessmentReloaded',
  'FocusLost',
  'FocusGained',
  'QuestionViewed',
  'QuestionNavigated',
  'AnswerSubmitted',
  'AssessmentSubmitted',
  'NavigationAway',
];

const eventTypeLabel = (eventType: AssessmentIntegrityEventType): string =>
  eventType.replace(/([a-z])([A-Z])/g, '$1 $2');

const formatElapsed = (milliseconds?: number): string => {
  if (milliseconds === undefined || milliseconds === null) return '—';
  if (milliseconds < 1000) return `${milliseconds} ms`;
  return `${(milliseconds / 1000).toFixed(1)} s`;
};

const formatMetadata = (metadataJson?: string): string => {
  if (!metadataJson) return '';

  try {
    return JSON.stringify(JSON.parse(metadataJson), null, 2);
  } catch {
    return metadataJson;
  }
};

const AssessmentIntegrity: React.FC = () => {
  const [events, setEvents] = useState<AssessmentIntegrityEvent[]>([]);
  const [filter, setFilter] = useState<AssessmentIntegrityEventFilter>({});
  const [draftExamTakerId, setDraftExamTakerId] = useState('');
  const [draftAssignmentId, setDraftAssignmentId] = useState('');
  const [draftEventType, setDraftEventType] = useState<AssessmentIntegrityEventType | ''>('');
  const [draftSuspiciousOnly, setDraftSuspiciousOnly] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AssessmentIntegrityEvent | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEvents = useCallback(async (page: number) => {
    setLoading(true);
    setError('');

    try {
      const response = await assessmentIntegrityService.getEvents(page, PAGE_SIZE, filter);
      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Unable to load assessment integrity events.');
      }

      setEvents(response.data.data);
      setCurrentPage(response.data.pageNumber);
      setTotalPages(response.data.totalPages);
      setTotalEvents(response.data.totalCount);
      setSelectedEvent(null);
    } catch (loadError: any) {
      setError(loadError?.response?.data?.message || loadError?.message || 'Unable to load assessment integrity events.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void loadEvents(currentPage);
  }, [currentPage, loadEvents]);

  const applyFilters = () => {
    setFilter({
      examTakerId: draftExamTakerId.trim() || undefined,
      assignmentId: draftAssignmentId.trim() || undefined,
      eventType: draftEventType || undefined,
      suspiciousOnly: draftSuspiciousOnly || undefined,
    });
    setCurrentPage(1);
  };

  const activeFilterCount = useMemo(() => Object.values(filter).filter(Boolean).length, [filter]);

  return (
    <div className={styles.container}>
      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>ASSESSMENT INTEGRITY</p>
          <h2 className={styles.title}>Review assessment activity</h2>
          <p className={styles.subtitle}>
            Review server-recorded focus changes, reloads, navigation, answer timing, and other signals.
            Flags support a human review; they are not automatic proof of misconduct.
          </p>
        </div>
        <button type="button" className={styles.refreshButton} onClick={() => void loadEvents(currentPage)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </section>

      <section className={styles.filterCard} aria-label="Integrity event filters">
        <div className={styles.filterHeader}>
          <div>
            <h3>Filter activity</h3>
            <p>{activeFilterCount === 0 ? 'Showing all recorded events.' : `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied.`}</p>
          </div>
          <span className={styles.filterHint}>Newest events first</span>
        </div>
        <div className={styles.filterGrid}>
          <label className={styles.field}>
            <span>Candidate ID or email</span>
            <input
              value={draftExamTakerId}
              onChange={event => setDraftExamTakerId(event.target.value)}
              placeholder="Search candidate"
              type="search"
            />
          </label>
          <label className={styles.field}>
            <span>Assignment ID</span>
            <input
              value={draftAssignmentId}
              onChange={event => setDraftAssignmentId(event.target.value)}
              placeholder="Optional GUID"
              type="text"
            />
          </label>
          <label className={styles.field}>
            <span>Event type</span>
            <select value={draftEventType} onChange={event => setDraftEventType(event.target.value as AssessmentIntegrityEventType | '')}>
              <option value="">All events</option>
              {eventTypes.map(eventType => (
                <option key={eventType} value={eventType}>{eventTypeLabel(eventType)}</option>
              ))}
            </select>
          </label>
          <label className={styles.checkboxField}>
            <input
              type="checkbox"
              checked={draftSuspiciousOnly}
              onChange={event => setDraftSuspiciousOnly(event.target.checked)}
            />
            <span>Suspicious signals only</span>
          </label>
          <button type="button" className={styles.applyButton} onClick={applyFilters} disabled={loading}>
            Apply filters
          </button>
        </div>
      </section>

      {error && <div className={styles.error} role="alert">{error}</div>}

      <div className={styles.summary}>
        <div>
          <strong>{totalEvents.toLocaleString()}</strong> recorded event{totalEvents === 1 ? '' : 's'}
        </div>
        {loading && <span>Loading…</span>}
      </div>

      <section className={styles.tableShell} aria-label="Assessment integrity events">
        {events.length === 0 && !loading ? (
          <div className={styles.emptyState}>
            <img src="/images/icons/shield.svg" alt="" />
            <h3>No integrity events found</h3>
            <p>Try clearing a filter or check back after an assessment attempt has started.</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Activity</th>
                <th>Candidate</th>
                <th>Assessment</th>
                <th>Timing</th>
                <th>Recorded</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id}>
                  <td>
                    <div className={styles.activityCell}>
                      <span className={styles.eventType}>{eventTypeLabel(event.eventType)}</span>
                      {event.questionId && <span className={styles.muted}>Question {event.questionId.slice(0, 8)}…</span>}
                    </div>
                  </td>
                  <td>
                    <div className={styles.candidateCell}>
                      <strong>{event.examTakerDisplayName || 'Unknown candidate'}</strong>
                      <span className={styles.muted}>{event.examTakerId}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.assessmentCell}>
                      <strong>{event.assignmentTitle || 'Untitled assignment'}</strong>
                      <span className={styles.muted}>{event.moduleTitle || 'Untitled module'}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.timingCell}>
                      <span>Client {formatElapsed(event.reportedElapsedMilliseconds)}</span>
                      <span>Server {formatElapsed(event.serverElapsedMilliseconds)}</span>
                    </div>
                  </td>
                  <td className={styles.dateCell}>{formatDateToLocal(event.recordedAtUtc)}</td>
                  <td>
                    <div className={styles.reviewCell}>
                      <span className={event.isSuspicious ? styles.flagged : styles.clear}>
                        {event.isSuspicious ? 'Review' : 'No flag'}
                      </span>
                      {(event.isSuspicious || event.metadataJson) && (
                        <button type="button" className={styles.viewButton} onClick={() => setSelectedEvent(event)}>
                          Details
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selectedEvent && (
        <section className={styles.detailCard} aria-label="Selected event details">
          <div className={styles.detailHeader}>
            <div>
              <p className={styles.eyebrow}>EVENT DETAILS</p>
              <h3>{eventTypeLabel(selectedEvent.eventType)}</h3>
            </div>
            <button type="button" className={styles.closeButton} onClick={() => setSelectedEvent(null)}>Close</button>
          </div>
          {selectedEvent.suspicionReason && (
            <div className={styles.reason}>
              <strong>Review reason</strong>
              <span>{selectedEvent.suspicionReason}</span>
            </div>
          )}
          <div className={styles.detailGrid}>
            <span><strong>Candidate</strong>{selectedEvent.examTakerDisplayName} ({selectedEvent.examTakerId})</span>
            <span><strong>Progress ID</strong>{selectedEvent.moduleProgressId}</span>
            <span><strong>Sequence</strong>{selectedEvent.sequenceNumber ?? '—'}</span>
            <span><strong>Client time</strong>{selectedEvent.clientOccurredAtUtc ? formatDateToLocal(selectedEvent.clientOccurredAtUtc) : '—'}</span>
          </div>
          {selectedEvent.metadataJson && (
            <pre className={styles.metadata}>{formatMetadata(selectedEvent.metadataJson)}</pre>
          )}
        </section>
      )}

      <div className={styles.pagination}>
        <button type="button" onClick={() => setCurrentPage(page => Math.max(1, page - 1))} disabled={currentPage <= 1 || loading}>
          Previous
        </button>
        <span>Page {currentPage} of {Math.max(totalPages, 1)}</span>
        <button type="button" onClick={() => setCurrentPage(page => page + 1)} disabled={currentPage >= totalPages || totalPages <= 1 || loading}>
          Next
        </button>
      </div>
    </div>
  );
};

export default AssessmentIntegrity;
